import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ConfirmationService} from 'primeng/primeng';
import {GlobalAppService} from '../../../../core/commons/global-app.service';
import {Router} from '@angular/router';
import {MatPaginator, MatSort, MatTableDataSource} from '@angular/material';
import {QueryOptions} from '../../../../core/commons/query.options';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {AbogadosProfileService} from 'app/fiscalia/service/abogados-profile.service';
import {Abogado} from 'app/fiscalia/domain/abogado';
import {MinistryService} from 'app/profile/service/ministry.service';
import {Observable} from 'rxjs/Observable';
import {Ministry} from 'app/profile/domain/ministry/ministry';
import {AbogadoContact} from '../../../domain/abogado-contact';
import {PageStateService} from '../../../../core/service/page-state.service';
import {filter, take} from 'rxjs/operators';

@Component({
    selector: 'app-abogados-profiles-list',
    templateUrl: './abogados-profiles-list.component.html',
    styleUrls: ['./abogados-profiles-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AbogadosProfilesListComponent implements OnInit, OnDestroy {
    // Ministries
    public ministries = [];
    filteredOptions: Observable<Ministry[]>;

    // Abogados
    abogados = new MatTableDataSource([]);
    queryOptions: QueryOptions;
    abogadosData: any = {};
    resultsLength = 0;

    // Message
    showEditor = false;
    selection: AbogadoContact[] = [];
    allContacts: AbogadoContact[] = [];
    reloadContacts = true;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    displayedColumns = [
        'name',
        'document',
        'ministry',
        'email',
        'phone',
        'actions',
        'select'
    ];
    formGroup: FormGroup;

    constructor(
        private abogadosListService: AbogadosProfileService,
        private ministryService: MinistryService,
        private confirmationService: ConfirmationService,
        private appService: GlobalAppService,
        private router: Router,
        private fb: FormBuilder,
        private pageState: PageStateService,
        private cd: ChangeDetectorRef
    ) {
        this.formGroup = this.fb.group({
            search: new FormControl(''),
            ministry: new FormControl('')
        });
    }

    ngOnInit() {
        this.pageState.setInitialPage();

        this.queryOptions = new QueryOptions();

        this.ministryService
            .getAll()
            .pipe(
                take(1)
            )
            .subscribe(ministries => (this.ministries = ministries));

        let state = this.pageState.loadState();

        state.pipe(filter(state => state !== null))
            .subscribe(pageState => {
                this.formGroup.get('ministry').setValue(pageState.state.ministry);
                this.formGroup.get('search').setValue(pageState.state.search);
                this.paginator.pageIndex = pageState.state.page;
                this.paginator.pageSize = pageState.state.size;
                this.queryOptions.page = pageState.state.page;
                this.queryOptions.size = pageState.state.size;
                this.applyFilter();
            });

        state.pipe(filter(state => state === null))
            .subscribe(state => {
                this.applyFilterAndReset();
            });

        this.paginator.page.subscribe(() => {
            this.queryOptions.page = this.paginator.pageIndex;
            this.queryOptions.size = this.paginator.pageSize;
            this.applyFilter();
        });

        this.formGroup.controls['search'].valueChanges
            .debounceTime(400)
            .subscribe(() => {
                this.reloadContacts = true;
                this.applyFilterAndReset();
            });

        this.formGroup.controls['ministry'].valueChanges.subscribe(() => {
            if (this.formGroup.controls['ministry'].value == '') {
                this.filteredOptions = this.getAllMinistries();
            } else {
                this.filteredOptions = this._filter(
                    this.formGroup.controls['ministry'].value
                );
            }
        });

        this.cd.markForCheck();
    }

    /*
      Métodos para filtrar por ministerios.
    */
    private getAllMinistries(): Observable<Ministry[]> {
        return Observable.of(this.ministries);
    }

    displayFn(ministry?: Ministry): string | undefined {
        return ministry ? ministry.name : undefined;
    }

    private _filter(value: string): Observable<Ministry[]> {
        const filterValue = value.toLowerCase();

        return Observable.of(
            this.ministries.filter(option =>
                option.name.toLowerCase().includes(filterValue)
            )
        );
    }

    /*
      Métodos para aplicar filtros
    */
    applyFilterAndReset() {
        this.paginator.pageIndex = 0;
        this.queryOptions.size = 5;
        this.queryOptions.page = 0;
        this.applyFilter();
    }

    applyFilter() {

        let ministryValue = this.formGroup.controls['ministry'].value.id;

        if (ministryValue != null)
            this.reloadContacts = true;

        this.abogadosListService
            .pagedSearch(
                this.formGroup.controls['search'].value,
                ministryValue,
                this.queryOptions
            )
            .subscribe(data => {
                this.abogadosData = data;
                this.abogados.data = this.abogadosData.content;
                this.resultsLength = this.abogadosData.totalElements;
            });

        if (this.reloadContacts) {
            this.abogadosListService
                .getContactInformation(
                    this.formGroup.controls['search'].value,
                    ministryValue
                )
                .subscribe(abogados => {
                    this.allContacts = abogados;
                    this.cd.markForCheck();
                });
            this.selection = [];
            this.reloadContacts = false;
        }
    }

    /*
      Seleccionar abogados
    */
    gotoDetail(abogado: Abogado) {
        this.router.navigate(['home', 'abogados-profiles', abogado.id]);
    }

    public isSendMessageButtonVisible(): boolean {
        const numSelected = this.selection.length;
        return numSelected > 0;
    }

    getAbogadosSelected(): AbogadoContact[] {
        return this.selection;
    }

    openMessageEditor(): void {
        this.showEditor = true;
    }

    // Whether the number of selected elements matches the total number of rows.
    isAllSelected() {
        const numSelected = this.selection.length;
        const numRows = this.allContacts.length;
        return numSelected === numRows;
    }

    // Selects all rows if they are not all selected; otherwise clear selection.
    masterToggle() {
        this.isAllSelected()
            ? this.selection = []
            : this.selection = this.allContacts;
    }

    isSelected(abogado: Abogado): boolean {
        return this.selection.find(selected => selected.id === abogado.id) != null;
    }

    toggleSelection(abogado: Abogado) {
        if (this.selection.find(selected => selected.id === abogado.id) != null) {
            this.selection = this.selection.filter(selected => selected.id !== abogado.id);
        } else {
            this.selection.push(new AbogadoContact(abogado));
            this.selection = [].concat( this.selection );
        }
        this.cd.markForCheck();
    }

    /*
      Clear inputs
    */
    isClearIconVisible(): boolean {
        if (this.formGroup.controls['search'].value != '') return true;
        else return false;
    }

    clearInputValue() {
        this.formGroup.patchValue({
            search: ''
        });
    }

    isClearIconVisible2(): boolean {
        if (this.formGroup.controls['ministry'].value != '') return true;
        else return false;
    }

    clearInputValue2() {
        this.formGroup.patchValue({
            ministry: ''
        });
    }

    ngOnDestroy(): void {
        const state = {
            page: this.queryOptions.page,
            size: this.queryOptions.size,
            search: this.formGroup.controls['search'].value,
            ministry: this.formGroup.controls['ministry'].value
        };
        this.pageState.saveState(state);
    }


}



// WEBPACK FOOTER //
// ./src/app/fiscalia/component/abogados-profiles/abogados-profiles-list/abogados-profiles-list.component.ts